import type {
    SketchDimensionFormulaCompileContext,
    SketchDimensionFormulaCompilerContract,
} from '../../../../types/sketch/solver/SketchDimensionFormulaCompiler';
import type {
    SketchParametricPathSegment,
    SketchParametricPointHandle,
} from '../../../../types/sketch/solver/SketchParametricTypes';
import {resolveSketchDimensionAngleGeometricValue} from '../../dimension/utils';
import {SketchParametricEquationFactory} from './SketchParametricEquationFactory';

/**
 * 草图尺寸公式编译器。
 *
 * 本类只负责把尺寸领域快照编译为统一参数方程，不采集运行时对象、不执行求解、
 * 不写回几何。driven、抑制或悬空尺寸只保留领域状态，不进入活动方程集合。
 */
export class SketchDimensionFormulaCompiler implements SketchDimensionFormulaCompilerContract {
    /**
     * 编译单条尺寸为参数方程。
     *
     * @param input 尺寸公式编译上下文。
     */
    public compile(input: SketchDimensionFormulaCompileContext): void {
        const {
            system, dimension, pointHandles, linePointHandles, centerPointHandles, radiusParameterIds,
            slotStructureParameterIds, splineLengthStates, ellipseGeometryStates, parabolaGeometryStates,
            edgeStateByAnyId,
            dimensionEquationIds, resolvedDimensionBranches, unsupportedDimensions,
        } = input;

        // driven 只测量不驱动；抑制/悬空尺寸不进入求解（02 计划 §7.4）。
        if (dimension.role !== 'driving' || dimension.suppressed || dimension.solveState === 'dangling') {
            return;
        }

        const equationId = `dimension:${dimension.dimensionId}`;
        const report = (reason: string): void => {
            unsupportedDimensions.push({id: dimension.dimensionId, kind: dimension.dimensionKind, reason});
        };
        /** 业务值 → 求解几何量（直径→半径、双倍距离→单侧距离）。 */
        const geometricTarget = dimension.parameter.nominalValue / dimension.parameter.valueScale;
        const commit = (equation: ReturnType<typeof SketchParametricEquationFactory.circleRadius>): void => {
            system.addEquation(equation);
            dimensionEquationIds.set(dimension.dimensionId, equationId);
        };
        /** 解析分支符号：显式分支优先，否则按当前几何符号推导并回报。 */
        const resolveBranchSign = (currentSigned: number): 1 | -1 => {
            if (dimension.branch.directionSign !== undefined) {
                return dimension.branch.directionSign;
            }

            const sign: 1 | -1 = currentSigned >= 0 ? 1 : -1;

            resolvedDimensionBranches.set(dimension.dimensionId, sign);

            return sign;
        };
        const resolvePointReference = (index: number): SketchParametricPointHandle | null => {
            const reference = dimension.references[index];

            return reference?.pointId ? pointHandles.get(reference.pointId) ?? null : null;
        };
        const resolveLineReference = (index: number):
            | readonly [SketchParametricPointHandle, SketchParametricPointHandle]
            | null => {
            const reference = dimension.references[index];

            return reference ? linePointHandles.get(reference.entityId) ?? null : null;
        };
        /**
         * 收集圆系引用的 Min/Max 半径偏置（ARC-001）：
         * 测量值 = 锚点距离 + Σ(sign×r)，Min=-1（近侧圆周）、Max=+1（远侧圆周）；
         * Center 不产生偏置。半径参数缺失时返回 null（由调用方报告 unsupported）。
         */
        const collectArcConditionOffsets = ():
            | readonly {readonly radiusParameterId: string; readonly sign: 1 | -1}[]
            | null => {
            const offsets: {radiusParameterId: string; sign: 1 | -1}[] = [];

            for (const reference of dimension.references) {
                if (reference.arcCondition !== 'min' && reference.arcCondition !== 'max') {
                    continue;
                }

                const radiusParameterId = radiusParameterIds.get(reference.entityId);

                if (!radiusParameterId) {
                    return null;
                }
                offsets.push({
                    radiusParameterId,
                    sign: reference.arcCondition === 'min' ? -1 : 1,
                });
            }

            return offsets;
        };

        switch (dimension.dimensionKind) {
            case 'point-distance': {
                const first = resolvePointReference(0);
                const second = resolvePointReference(1);

                if (!first || !second) {
                    report('两点距离尺寸引用点不在 island 点集中');
                    return;
                }

                const orientation = dimension.branch.orientation ?? 'aligned';
                const arcOffsets = collectArcConditionOffsets();

                if (!arcOffsets) {
                    report('Min/Max 附着引用的圆/弧缺少半径参数');
                    return;
                }
                if (arcOffsets.length > 0) {
                    // 水平/竖直距离与 Min/Max 附着的组合矩阵属 V 级待实机（01 §15.6）。
                    if (orientation !== 'aligned') {
                        report('水平/竖直距离与 Min/Max 附着的组合待实机矩阵确认');
                        return;
                    }
                    commit(SketchParametricEquationFactory.pointPointDistanceWithRadiusOffsets(
                        equationId, first, second, arcOffsets, geometricTarget,
                    ));
                    return;
                }

                if (orientation === 'horizontal') {
                    const sign = resolveBranchSign(system.read(second.xId) - system.read(first.xId));

                    commit(SketchParametricEquationFactory.pointPointHorizontalDistance(
                        equationId, first, second, geometricTarget, sign,
                    ));
                    return;
                }
                if (orientation === 'vertical') {
                    const sign = resolveBranchSign(system.read(second.yId) - system.read(first.yId));

                    commit(SketchParametricEquationFactory.pointPointVerticalDistance(
                        equationId, first, second, geometricTarget, sign,
                    ));
                    return;
                }
                commit(SketchParametricEquationFactory.pointPointDistance(equationId, first, second, geometricTarget));
                return;
            }
            case 'length': {
                const linePoints = resolveLineReference(0);

                if (!linePoints) {
                    report('单线长度尺寸引用无法解析为线两端点');
                    return;
                }

                /** H/V 尺寸命令对单线的固定方向语义（阶段 8）：两端点水平/竖直距离。 */
                const lengthOrientation = dimension.branch.orientation ?? 'aligned';

                if (lengthOrientation === 'horizontal') {
                    const sign = resolveBranchSign(system.read(linePoints[1].xId) - system.read(linePoints[0].xId));

                    commit(SketchParametricEquationFactory.pointPointHorizontalDistance(
                        equationId, linePoints[0], linePoints[1], geometricTarget, sign,
                    ));
                    return;
                }
                if (lengthOrientation === 'vertical') {
                    const sign = resolveBranchSign(system.read(linePoints[1].yId) - system.read(linePoints[0].yId));

                    commit(SketchParametricEquationFactory.pointPointVerticalDistance(
                        equationId, linePoints[0], linePoints[1], geometricTarget, sign,
                    ));
                    return;
                }
                commit(SketchParametricEquationFactory.pointPointDistance(
                    equationId, linePoints[0], linePoints[1], geometricTarget,
                ));
                return;
            }
            case 'path-length': {
                /**
                 * 路径总长（阶段 8）：链各段之和。线段=两端点直线长；
                 * 弧段=半径参数 × 扫角（端点/圆心/走向来自快照结构角色声明）；
                 * 样条段=初始采样弧长 × 缩放参数（与样条总长同契约）。
                 * 曾只支持线段——含弧路径整岛按未支持降级，殃及同岛其它
                 * driving 尺寸（槽口自带尺寸改值一起报错）。
                 */
                const segments: SketchParametricPathSegment[] = [];

                for (let index = 0; index < dimension.references.length; index += 1) {
                    const linePoints = resolveLineReference(index);

                    if (linePoints) {
                        segments.push({kind: 'line', start: linePoints[0], end: linePoints[1]});
                        continue;
                    }

                    const entityId = dimension.references[index]?.entityId ?? '';
                    const edgeState = edgeStateByAnyId.get(entityId);
                    const geometry = edgeState?.geometry as Record<string, unknown> | undefined;

                    /**
                     * 槽口成员弧不入弧长方程：端帽弧的半径参数与槽口半宽是两套参数
                     * 且无方程互锁（结构声明排除了 pointOnCircle），弧长残差会把自由
                     * 半径参数拉走、写回后端帽与槽口结构不自洽。按未支持上报——
                     * 创建链路会自动转从动，不殃及同岛其它 driving 尺寸。
                     */
                    if (geometry?.slotStructure || geometry?.arcSlotStructure) {
                        report('路径总长包含槽口成员弧段，暂不支持参数驱动');
                        return;
                    }

                    // 弧段：端点/圆心句柄 + 半径参数 + 走向。
                    const startHandle = typeof geometry?.startPointId === 'string'
                        ? pointHandles.get(geometry.startPointId)
                        : undefined;
                    const endHandle = typeof geometry?.endPointId === 'string'
                        ? pointHandles.get(geometry.endPointId)
                        : undefined;
                    const centerHandle = edgeState ? centerPointHandles.get(edgeState.entityId) : undefined;
                    const radiusParameterId = edgeState ? radiusParameterIds.get(edgeState.entityId) : undefined;

                    if (startHandle && endHandle && centerHandle && radiusParameterId) {
                        const arcDefinition = geometry?.arcDefinition as Record<string, unknown> | undefined;

                        segments.push({
                            kind: 'arc',
                            start: startHandle,
                            end: endHandle,
                            center: centerHandle,
                            radiusParameterId,
                            clockwise: arcDefinition?.clockwise === true,
                        });
                        continue;
                    }

                    // 样条段：整体缩放承载弧长。
                    const splineState = edgeState ? splineLengthStates.get(edgeState.entityId) : undefined;

                    if (splineState) {
                        segments.push({
                            kind: 'spline',
                            baseLength: splineState.baseLength,
                            scaleParameterId: splineState.scaleParameterId,
                        });
                        continue;
                    }

                    report('路径总长存在无法解析为线段/弧/样条段的引用');
                    return;
                }
                commit(SketchParametricEquationFactory.pathLength(equationId, segments, geometricTarget));
                return;
            }
            case 'spline-length': {
                /**
                 * 样条总长：弧长 = 初始采样弧长 × 整体缩放系数。
                 *
                 * 样条曲线由 Worker 内 Puppy CAD 内核插值算出，残差里无法同步复算；
                 * 而弧长对相似变换一次齐次，缩放是精确关系，故用它承载总长驱动。
                 * 结构声明缺失（控制点句柄不全等）时按未支持上报，不静默失效。
                 */
                const entityId = dimension.references[0]?.entityId;
                const splineState = entityId ? splineLengthStates.get(entityId) : undefined;

                if (!splineState) {
                    report('样条总长尺寸缺少样条结构声明（控制点或采样弧长不可解析）');
                    return;
                }
                commit(SketchParametricEquationFactory.splineLength(
                    equationId,
                    splineState.scaleParameterId,
                    splineState.baseLength,
                    geometricTarget,
                ));
                return;
            }
            case 'point-line-distance':
            case 'line-line-distance':
            case 'doubled-distance': {
                /**
                 * 三类共用点线垂距方程：
                 * - 点线距：点 + 线；
                 * - 平行线距：第二线起点到第一线的垂距；
                 * - 中心线双倍距离（SD-SEL-014/015）：被测方（引用[0]，点或线起点）到中心线
                 *   （约定引用[1]）的垂距，业务值 = 2×垂距由 valueScale 换算承载。
                 */
                const isPointLine = dimension.dimensionKind === 'point-line-distance';
                const isDoubled = dimension.dimensionKind === 'doubled-distance';
                const point = isPointLine
                    ? resolvePointReference(0) ?? resolvePointReference(1)
                    : resolvePointReference(0)
                        ?? linePointHandles.get(dimension.references[isDoubled ? 0 : 1]?.entityId ?? '')?.[0]
                        ?? null;
                const lineReferenceIndex = isPointLine
                    ? (dimension.references[0]?.pointId ? 1 : 0)
                    : (isDoubled ? 1 : 0);
                const linePoints = resolveLineReference(lineReferenceIndex);

                if (!point || !linePoints) {
                    report(`${isPointLine ? '点线距' : isDoubled ? '中心线双倍距离' : '平行线距'}尺寸引用无法解析`);
                    return;
                }

                const arcOffsets = collectArcConditionOffsets();

                if (!arcOffsets) {
                    report('Min/Max 附着引用的圆/弧缺少半径参数');
                    return;
                }

                const direction = {
                    x: system.read(linePoints[1].xId) - system.read(linePoints[0].xId),
                    y: system.read(linePoints[1].yId) - system.read(linePoints[0].yId),
                };
                const currentSigned = direction.x * (system.read(point.yId) - system.read(linePoints[0].yId))
                    - direction.y * (system.read(point.xId) - system.read(linePoints[0].xId));
                const sign = resolveBranchSign(currentSigned);

                if (arcOffsets.length > 0) {
                    commit(SketchParametricEquationFactory.pointLineDistanceWithRadiusOffsets(
                        equationId, point, linePoints[0], linePoints[1], arcOffsets, geometricTarget, sign,
                    ));
                    return;
                }
                commit(SketchParametricEquationFactory.pointLineDistance(
                    equationId, point, linePoints[0], linePoints[1], geometricTarget, sign,
                ));
                return;
            }
            case 'arc-length': {
                // 真弧长（SD-SEL-008）：圆心 + 两端点引用，走向分支延续原弧方向。
                const centerReference = dimension.references.find((reference) => reference.topologyRole === 'center');
                const endpointReferences = dimension.references.filter((reference) => (
                    reference !== centerReference && Boolean(reference.pointId)
                ));
                const center = centerReference?.pointId ? pointHandles.get(centerReference.pointId) ?? null : null;
                const startPoint = endpointReferences[0]?.pointId
                    ? pointHandles.get(endpointReferences[0].pointId) ?? null
                    : null;
                const endPoint = endpointReferences[1]?.pointId
                    ? pointHandles.get(endpointReferences[1].pointId) ?? null
                    : null;

                if (!center || !startPoint || !endPoint) {
                    report('真弧长尺寸需要可解析的圆心与两个端点引用');
                    return;
                }
                commit(SketchParametricEquationFactory.arcLength(
                    equationId, center, startPoint, endPoint, geometricTarget,
                    dimension.branch.directionSign ?? 1,
                ));
                return;
            }
            case 'curve-offset-distance': {
                // 曲线对偏移距离（等距实体自动尺寸）：
                // - 圆/圆弧：锁定两半径差 r₂ − r₁ = ±d（配合圆心重合关系即处处等距）；
                // - 椭圆：锁定长/短轴方向平行 + 两半轴长度差 = ±d（配合圆心重合关系）。
                const firstEntityId = dimension.references[0]?.entityId ?? '';
                const secondEntityId = dimension.references[1]?.entityId ?? '';
                const firstRadiusParameterId = radiusParameterIds.get(firstEntityId);
                const secondRadiusParameterId = radiusParameterIds.get(secondEntityId);

                if (firstRadiusParameterId && secondRadiusParameterId) {
                    // 圆/圆弧：半径差方程（纯标量）
                    const currentDifference = system.read(secondRadiusParameterId)
                        - system.read(firstRadiusParameterId);
                    const sign = dimension.branch.directionSign ?? (currentDifference >= 0 ? 1 : -1);

                    resolvedDimensionBranches.set(dimension.dimensionId, sign);
                    commit(SketchParametricEquationFactory.scalarDifference(
                        equationId,
                        secondRadiusParameterId,
                        firstRadiusParameterId,
                        sign * geometricTarget,
                    ));
                    return;
                }

                const firstEllipse = ellipseGeometryStates.get(firstEntityId);
                const secondEllipse = ellipseGeometryStates.get(secondEntityId);

                if (firstEllipse && secondEllipse) {
                    // 椭圆：轴方向平行（方向分量方程，自动锁定同向/反向分支）
                    const firstMajorLength = Math.hypot(
                        system.read(firstEllipse.majorPositive.xId) - system.read(firstEllipse.center.xId),
                        system.read(firstEllipse.majorPositive.yId) - system.read(firstEllipse.center.yId),
                    );
                    const secondMajorLength = Math.hypot(
                        system.read(secondEllipse.majorPositive.xId) - system.read(secondEllipse.center.xId),
                        system.read(secondEllipse.majorPositive.yId) - system.read(secondEllipse.center.yId),
                    );
                    const sign = dimension.branch.directionSign
                        ?? (secondMajorLength - firstMajorLength >= 0 ? 1 : -1);

                    resolvedDimensionBranches.set(dimension.dimensionId, sign);
                    // 长轴平行：等距椭圆长轴方向跟随源椭圆
                    system.addEquation(SketchParametricEquationFactory.parallel(
                        `${equationId}:major-parallel`,
                        firstEllipse.center,
                        firstEllipse.majorPositive,
                        secondEllipse.center,
                        secondEllipse.majorPositive,
                    ));
                    // 长半轴差 = ±d
                    system.addEquation(SketchParametricEquationFactory.distanceDifference(
                        `${equationId}:major-distance`,
                        secondEllipse.center,
                        secondEllipse.majorPositive,
                        firstEllipse.center,
                        firstEllipse.majorPositive,
                        sign * geometricTarget,
                    ));
                    // 短半轴差 = ±d（短轴与长轴垂直由椭圆自身结构方程保证）
                    system.addEquation(SketchParametricEquationFactory.distanceDifference(
                        `${equationId}:minor-distance`,
                        secondEllipse.center,
                        secondEllipse.minorPositive,
                        firstEllipse.center,
                        firstEllipse.minorPositive,
                        sign * geometricTarget,
                    ));
                    dimensionEquationIds.set(dimension.dimensionId, `${equationId}:major-distance`);
                    return;
                }

                const firstParabola = parabolaGeometryStates.get(firstEntityId);
                const secondParabola = parabolaGeometryStates.get(secondEntityId);

                if (firstParabola && secondParabola) {
                    // 抛物线：等距构造保证两抛物线同轴、等焦距、顶点沿轴偏移 d。
                    // 这里解析第二抛物线顶点相对第一抛物线轴向的有向距离，锁定该距离 = ±d；
                    // 焦点—顶点向量相等由 parabolaOffsetDistance 方程一并锁定。
                    const firstAxis = {
                        x: system.read(firstParabola.focal.xId) - system.read(firstParabola.apex.xId),
                        y: system.read(firstParabola.focal.yId) - system.read(firstParabola.apex.yId),
                    };
                    const axisLength = Math.hypot(firstAxis.x, firstAxis.y);

                    if (axisLength < 1e-12) {
                        report('抛物线偏移距离尺寸引用退化（焦点与顶点重合）');
                        return;
                    }
                    const apexDeltaX = system.read(secondParabola.apex.xId)
                        - system.read(firstParabola.apex.xId);
                    const apexDeltaY = system.read(secondParabola.apex.yId)
                        - system.read(firstParabola.apex.yId);
                    const currentSigned = (apexDeltaX * firstAxis.x + apexDeltaY * firstAxis.y) / axisLength;
                    const sign = resolveBranchSign(currentSigned);

                    resolvedDimensionBranches.set(dimension.dimensionId, sign);
                    commit(SketchParametricEquationFactory.parabolaOffsetDistance(
                        equationId,
                        firstParabola.focal,
                        firstParabola.apex,
                        secondParabola.focal,
                        secondParabola.apex,
                        geometricTarget,
                        sign,
                    ));
                    return;
                }

                // 样条参与的偏移对（样条对、抛物线源+样条偏移等）由派生重算路径
                // （CurveOffsetDerivedSyncService / OffsetChainDerivedSyncService）接管——
                // 本编译层不参与：不加方程、也不报未支持，避免 island 被整体拒绝。
                // 抛物线等距（方案A）输出样条后，源侧仍是抛物线，因此任一侧为
                // 样条即视为派生服务管辖（同型对已在上方圆/椭圆/抛物线分支命中）。
                const firstEdge = edgeStateByAnyId.get(firstEntityId);
                const secondEdge = edgeStateByAnyId.get(secondEntityId);
                const isSplineEdge = (edge: typeof firstEdge): boolean => {
                    const kind = edge ? String(edge.entityKind).toLowerCase() : '';
                    return kind === 'spline' || kind === 'stylespline';
                };

                if (isSplineEdge(firstEdge) || isSplineEdge(secondEdge)) {
                    return;
                }

                report('曲线对偏移距离引用无法解析为圆/弧半径或椭圆基准轴状态');
                return;
            }
            case 'symmetric-angle':
            case 'angle': {
                const firstPoints = resolveLineReference(0);
                const secondPoints = resolveLineReference(1);

                if (!firstPoints || !secondPoints) {
                    report('两线角尺寸引用无法解析为两条线的端点');
                    return;
                }

                const firstDirection = {
                    x: system.read(firstPoints[1].xId) - system.read(firstPoints[0].xId),
                    y: system.read(firstPoints[1].yId) - system.read(firstPoints[0].yId),
                };
                const secondDirection = {
                    x: system.read(secondPoints[1].xId) - system.read(secondPoints[0].xId),
                    y: system.read(secondPoints[1].yId) - system.read(secondPoints[0].yId),
                };
                const currentSigned = Math.atan2(
                    firstDirection.x * secondDirection.y - firstDirection.y * secondDirection.x,
                    firstDirection.x * secondDirection.x + firstDirection.y * secondDirection.y,
                );
                const sign = resolveBranchSign(currentSigned);
                const angleGeometricTarget = resolveSketchDimensionAngleGeometricValue(
                    dimension.parameter.nominalValue,
                    dimension.parameter.valueScale,
                    dimension.branch.angleQuadrant,
                );

                commit(SketchParametricEquationFactory.lineLineAngle(
                    equationId,
                    firstPoints[0], firstPoints[1],
                    secondPoints[0], secondPoints[1],
                    angleGeometricTarget, sign,
                ));
                return;
            }
            case 'three-point-angle': {
                /**
                 * 三点角落在圆弧槽口的极坐标锚点（弧心 + 两端帽圆心）上时，
                 * **直接约束槽口自己的扫角参数**，不要走两射线夹角。
                 *
                 * 两射线夹角只能定出 (−π, π] 的有向角，270° 与 90° 在它眼里是同一个配置
                 * （wrapToPi 之后完全等价），于是优角槽口改角度时要么原地不动、
                 * 要么被拉到差 180° 的分支上——实测 270°→90° 报「未收敛，残差 3.14159」，
                 * 那个 π 就是这条二义留下的指纹。
                 * 槽口的起止角参数是**不做 2π 归一**的连续量，跨角由它们直接相减得到，
                 * 优角/劣角天然可分。
                 */
                const angleAnchorHandles = dimension.references
                    .map((_reference, index) => resolvePointReference(index))
                    .filter((handle): handle is SketchParametricPointHandle => Boolean(handle));
                const slotAngle = [...slotStructureParameterIds.values()].find((parameters) => {
                    const anchors = parameters.polarAnchorHandles;

                    return Boolean(anchors)
                        && anchors!.length === angleAnchorHandles.length
                        && anchors!.every((anchor) => angleAnchorHandles.includes(anchor));
                });

                if (slotAngle?.startAngleParameterId && slotAngle.endAngleParameterId) {
                    const scale = slotAngle.angleScale ?? 1;
                    const seedSweep = system.read(slotAngle.endAngleParameterId)
                        - system.read(slotAngle.startAngleParameterId);
                    /** 起止角连续、扫角符号不会在求解中翻转，按种子定一次即可。 */
                    const sweepSign = seedSweep >= 0 ? 1 : -1;

                    commit(SketchParametricEquationFactory.scalarSpanTarget(
                        equationId,
                        slotAngle.endAngleParameterId,
                        slotAngle.startAngleParameterId,
                        sweepSign * geometricTarget * scale,
                    ));
                    return;
                }

                // 第一引用是角顶点，第二/第三引用定义两射线（01 基线 SD-SEL-006）。
                const vertex = resolvePointReference(0);
                const firstRay = resolvePointReference(1);
                const secondRay = resolvePointReference(2);

                if (!vertex || !firstRay || !secondRay) {
                    report('三点角尺寸引用点不在 island 点集中');
                    return;
                }

                const firstDirection = {
                    x: system.read(firstRay.xId) - system.read(vertex.xId),
                    y: system.read(firstRay.yId) - system.read(vertex.yId),
                };
                const secondDirection = {
                    x: system.read(secondRay.xId) - system.read(vertex.xId),
                    y: system.read(secondRay.yId) - system.read(vertex.yId),
                };
                const currentSigned = Math.atan2(
                    firstDirection.x * secondDirection.y - firstDirection.y * secondDirection.x,
                    firstDirection.x * secondDirection.x + firstDirection.y * secondDirection.y,
                );
                const sign = resolveBranchSign(currentSigned);

                commit(SketchParametricEquationFactory.lineLineAngle(
                    equationId,
                    vertex, firstRay,
                    vertex, secondRay,
                    geometricTarget, sign,
                ));
                return;
            }
            case 'radius':
            case 'diameter': {
                const entityId = dimension.references[0]?.entityId ?? '';
                const radiusParameterId = radiusParameterIds.get(entityId);

                if (!radiusParameterId) {
                    report('半径/直径尺寸引用的圆/弧没有半径参数');
                    return;
                }
                commit(SketchParametricEquationFactory.circleRadius(equationId, radiusParameterId, geometricTarget));
                return;
            }
            default:
                report('阶段 3 参数内核暂不编译该尺寸类型（高级 2D 语义见阶段 6）');
        }
    }
}
